import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileX2, SearchX } from "lucide-react";
import { FeedbackState, type DataTableColumn } from "@h2j-data-team/ui";
import type { QvdFile, QvdFilesSort, QvdRunDiffSide } from "@repo/shared";

import { formatBytes } from "@/lib/health-format";
import { qvdDiffFilesInfiniteQueryOptions } from "@/features/qvd-analysis/api";
import { VirtualRowList } from "@/components/shared/VirtualRowList";
import { ListError, ListLoading, QvdListToolbar, QvdPathLabel, SpaceLink } from "./qvd-list-parts";
import { SERVER_SORT_DIRECTION, useDebouncedValue } from "./qvd-list-utils";

// One side of the run diff, listed with the same server-side search/space/sort
// and virtualized paging as the inventory table. No consumers column: consumer
// evidence belongs to a single run, and a removed row's evidence is the base
// run's — comparing the two here would be misleading.
export function QvdDiffFilesTable({
  runId,
  baseId,
  side,
  spaceOptions,
  totalCount,
}: {
  runId: string;
  baseId: string;
  side: QvdRunDiffSide;
  spaceOptions: string[];
  totalCount: number;
}) {
  const { t } = useTranslation("qvd-analysis");
  const [search, setSearch] = useState("");
  const [spaceFilter, setSpaceFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<QvdFilesSort>("size");
  const debouncedSearch = useDebouncedValue(search, 300);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    qvdDiffFilesInfiniteQueryOptions(runId, baseId, side, {
      search: debouncedSearch,
      space: spaceFilter[0] ?? "",
      sort: sortKey,
    }),
  );

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const filtering = !!debouncedSearch || spaceFilter.length > 0;

  const columns = useMemo<DataTableColumn<QvdFile>[]>(
    () => [
      {
        id: "name",
        header: t("footprint.cols.file"),
        sort: "text",
        cell: (f) => <QvdPathLabel path={f.path} />,
      },
      {
        id: "type",
        header: t("footprint.cols.fileType"),
        width: 100,
        cell: (f) => <span className="text-dim">{f.ext ? f.ext.toUpperCase() : "—"}</span>,
      },
      {
        id: "space",
        header: t("footprint.cols.space"),
        width: 200,
        cell: (f) => (
          <span className="text-dim">
            <SpaceLink spaceName={f.spaceName} />
          </span>
        ),
      },
      {
        id: "size",
        header: t("footprint.cols.size"),
        width: 120,
        align: "end",
        variant: "numeric",
        sort: "number",
        cell: (f) => formatBytes(f.sizeBytes),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col">
      <QvdListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t("files.searchPlaceholder")}
        spaceOptions={spaceOptions}
        spaceFilter={spaceFilter}
        onSpaceFilter={setSpaceFilter}
        singleSpace
      />

      <p className="pb-2 text-sm text-dim">
        {t(`compare.files.summary.${side}`, { count: totalCount })}{" "}
        <span className="text-faint">{t(`compare.files.note.${side}`)}</span>
      </p>

      {isLoading ? (
        <ListLoading />
      ) : isError ? (
        <ListError error={error} title={t("files.error")} onRetry={() => void refetch()} />
      ) : rows.length === 0 ? (
        <FeedbackState
          state={filtering ? "no-results" : "empty"}
          icon={filtering ? <SearchX /> : <FileX2 />}
          title={filtering ? t("files.empty.filteredTitle") : t(`compare.files.empty.${side}`)}
          description={
            filtering
              ? t("files.empty.filteredDescription")
              : t(`compare.files.emptyDescription.${side}`)
          }
        />
      ) : (
        <VirtualRowList
          label={t(`compare.tabs.${side}`)}
          columns={columns}
          rows={rows}
          getKey={(f) => f.id}
          sort={{ id: sortKey, direction: SERVER_SORT_DIRECTION[sortKey] }}
          onSort={(column) =>
            setSortKey(column.id === sortKey ? "size" : (column.id as QvdFilesSort))
          }
          onReachEnd={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          loadingMoreLabel={isFetchingNextPage ? t("loadingMore") : undefined}
        />
      )}
    </div>
  );
}
